import styled from "styled-components";
import { Spacing } from "../theme/Spacing";
import { AtiraCheckBoxGroup } from "./AtiraCheckBoxGroup";
import { Flex } from "./Flex";
import { Text } from "./Text";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../redux/store";
import { permissionSliceSelectors } from "../redux/permission/permission.selector";
import { AtiraIcon } from "./AtiraIcon";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";
import { Rounded } from "../theme/Rounded";
import { ImageCreateEdit } from "./ImageCreateEdit";
import { TestIDs } from "../tests/types/testIds";

const UserProfileWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Rounded.circle};
  width: 1.5rem;
  height: 1.5rem;
  overflow: hidden;
  object-fit: contain;
`;

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: column;
  gap: ${Spacing.s};
`;

type Props = {
  value: string[] | undefined;
  onChange?: (selected: string[]) => void;
  disabled?: boolean;
};

export const EntityAssignCheckbox: React.FC<Props> = ({
  onChange,
  value,
  disabled,
}) => {
  const { t } = useTranslation();

  const members = useAppSelector(permissionSliceSelectors.selectMembers).filter(
    (m) => (disabled ? value?.includes(m._id) : true),
  );

  if (disabled) {
    if (!members.length) {
      return null;
    }

    return (
      <Flex flexDirection="column" gap="s">
        <Text fontWeight={"bold"}>{t("common.assignees")}</Text>
        {members.map((m) => (
          <Flex key={m._id} alignItems="center" gap="s">
            <UserProfileWrapper>
              {m.picture ? (
                <ImageCreateEdit src={m.picture} />
              ) : (
                <AtiraIcon icon={faCircleUser} color="main" size="2xl" />
              )}
            </UserProfileWrapper>
            <Text>{m.name}</Text>
          </Flex>
        ))}
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="s">
      <Text>{t("common.assignees")}</Text>
      <StyledAtiraCheckBoxGroup
        onChange={(selected) => onChange?.(selected as string[])}
        options={members.map((m) => ({
          label: m.name,
          value: m._id,
          "data-testid": TestIDs.permissions.entityAssignCheckbox.option(m._id),
        }))}
        value={value || []}
        style={{ opacity: disabled ? 0.5 : 1 }}
      />
    </Flex>
  );
};
